import React from 'react'
import type { PivotLayout, PivotLayoutField } from '@ztechapps/common.contract'
import type { PivotFilterChip as PivotFilterChipItem } from '../pivot/listPivotFilterChips'
import type { PivotData, PivotFilterSetting } from '../pivot/types'
import { HorizontalStack } from '@ztechapps/webui.util'
import { PivotFilterModal } from '../FieldPanel/PivotFilterModal'
import { PIVOT_FILTER_ALL_TEXT } from '../pivot/constants'
import { listPivotConditionChips } from '../pivot/listPivotConditionChips'
import { listPivotValueFilterChips } from '../pivot/listPivotValueFilterChips'
import { PivotFilterChip } from './PivotFilterChip'

/** Props for the pivot filter bar. */
export type PivotFilterBarProps = {
  chips: PivotFilterChipItem[]
  layout: Pick<PivotLayout, 'rows' | 'columns' | 'measures' | 'filters'>
  data: PivotData
  timeZone: string
  onFilter: (field: PivotLayoutField, setting: PivotFilterSetting | null) => void
}

/**
 * The filters that are set, as chips each reopening the filter dialog of its field and each with a clear action: a
 * member selection on a filter-only field (a field the grid shows has it in the grid header), a Condition, a Top /
 * Bottom N, or a Value comparison.
 */
export const PivotFilterBar = ({ chips, layout, data, timeZone, onFilter }: PivotFilterBarProps) => {
  const chipKey = (filter: PivotLayoutField, part: string) => `${filter.field}|${filter.dateGroup ?? ''}|${part}`
  const dialog = (filter: PivotLayoutField, chip: (onOpen: () => void) => React.ReactNode) => (
    <PivotFilterModal
      key={chipKey(filter, 'chip')}
      data={data}
      field={filter}
      layout={layout}
      timeZone={timeZone}
      onApply={(setting) => onFilter(filter, setting)}
      trigger={({ onClick }) => chip(onClick)}
    />
  )

  return (
    // grows into the space the toolbar's right cluster leaves, so the chips and the toolbar share one row
    // min-width 0: a flex item never shrinks below its content, so a long chip would widen this row past the screen
    <HorizontalStack gap={2} className="flex-wrap flex-grow-1" style={{ minWidth: 0 }}>
      {chips.map(({ filter, label, text }) =>
        dialog(filter, (onOpen) => (
          <PivotFilterChip
            label={label}
            text={text}
            isActive={text !== PIVOT_FILTER_ALL_TEXT}
            onOpen={onOpen}
            onClear={() => onFilter(filter, null)}
          />
        ))
      )}
      {listPivotConditionChips({ data, layout }).map(({ filter, label, text }) =>
        dialog(filter, (onOpen) => (
          <PivotFilterChip label={label} text={text} isActive onOpen={onOpen} onClear={() => onFilter(filter, null)} />
        ))
      )}
      {listPivotValueFilterChips(layout).map(({ filter, label }) =>
        dialog(filter, (onOpen) => (
          <PivotFilterChip label={label} isActive onOpen={onOpen} onClear={() => onFilter(filter, null)} />
        ))
      )}
    </HorizontalStack>
  )
}
